/**
 * Visual Settings Slice
 * Handles background, playlist, UI settings, and effects
 */

import { BACKGROUNDS, getDefaultBackground } from '../../data/constants';

// Helper to detect device type
export const getIsMobile = () => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768 || window.matchMedia('(orientation: portrait)').matches;
};

// Helper to validate background compatibility
export const getValidBackgroundForDevice = (backgroundId: string, isMobile: boolean): string => {
    const background = BACKGROUNDS.find((bg) => bg.id === backgroundId);
    if (!background) return getDefaultBackground(isMobile);

    const requiredOrientation = isMobile ? 'vertical' : 'horizontal';
    if (background.orientation !== requiredOrientation) {
        return getDefaultBackground(isMobile);
    }

    return backgroundId;
};

export interface VisualSlice {
    background: string;
    backgroundMobile: string;
    backgroundDesktop: string;
    playlist: 'lofi' | 'synthwave';
    autoHideUI: boolean;
    snowEnabled: boolean;
    setBackground: (background: string) => void;
    setAutoHideUI: (enabled: boolean) => void;
    setPlaylist: (playlist: 'lofi' | 'synthwave') => void;
    toggleSnow: () => void;
}

export const createVisualSlice = (
    set: (partial: Partial<VisualSlice> | ((state: VisualSlice) => Partial<VisualSlice>)) => void,
    defaultBackground: string
): VisualSlice => ({
    background: getValidBackgroundForDevice(defaultBackground, getIsMobile()),
    backgroundMobile: getDefaultBackground(true),
    backgroundDesktop: getDefaultBackground(false),
    playlist: 'lofi',
    autoHideUI: false,
    snowEnabled: true,

    setBackground: (background) => {
        const isMobile = getIsMobile();
        const validBackground = getValidBackgroundForDevice(background, isMobile);

        set((state) => ({
            background: validBackground,
            backgroundMobile: isMobile ? validBackground : state.backgroundMobile,
            backgroundDesktop: !isMobile ? validBackground : state.backgroundDesktop,
        }));
    },

    setAutoHideUI: (enabled) => set({ autoHideUI: enabled }),

    setPlaylist: (playlist) => set({ playlist }),

    toggleSnow: () => set((state) => ({ snowEnabled: !state.snowEnabled })),
});
